import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  FileText,
  ArrowLeft,
  Upload,
  Trash2,
  Download,
  Share2,
  EyeOff,
  Calendar,
  Loader2,
  AlertCircle,
  CheckCircle2,
} from 'lucide-react';
import { DocumentUploadModal } from '../components/DocumentUploadModal';
import { ComplianceBadge } from '../components/ComplianceBadge';
import { DocumentExpiryWarning } from '../components/DocumentExpiryWarning';
import { UpgradeHintCard } from '../components/UpgradeHintCard';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { formatDateForDisplay, formatPropertyAddress } from '../lib/utils';
import { dismissUpgradePrompt, getUpgradePromptsForPlacement } from '../lib/upgradeHints';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ALL_TENANCIES_VALUE = '__all_tenancies__';

const DOCUMENT_TYPE_DISPLAY = {
  EPC: 'Energy Performance Certificate',
  GasSafety: 'Gas Safety Certificate',
  EICR: 'Electrical Installation Condition Report',
  DepositProtection: 'Deposit Protection Certificate',
  PrescribedInfo: 'Prescribed Information',
  LegionellaAssessment: 'Legionella Assessment',
  HMOLicence: 'HMO Licence',
  CouncilLicence: 'Council Licence',
  FireRiskAssessment: 'Fire Risk Assessment',
  PATTesting: 'PAT Testing',
  TenancyAgreement: 'Tenancy Agreement',
  GuarantorAgreement: 'Guarantor Agreement',
  InventoryCheckIn: 'Inventory Check-in',
  InventoryCheckOut: 'Inventory Check-out',
  Section8Notice: 'Section 8 Notice',
  Section13Notice: 'Section 13 Notice',
  HowToRent: 'How To Rent Guide',
  RRAInformationSheet: 'Renters’ Rights Act Information Sheet',
  DispatchReceipt: 'Document Delivery Record',
  ICORegistration: 'ICO Registration',
  RentSchedule: 'Rent Schedule',
  HoldingDepositAgreement: 'Holding Deposit Agreement',
  ArrearsLetter: 'Arrears Letter',
  TenantID: 'Tenant ID Document',
  RightToRentDoc: 'Right to Rent Document',
  ProofOfIncome: 'Proof of Income',
  EmploymentReference: 'Employment Reference',
  PreviousLandlordReference: 'Previous Landlord Reference',
  GuarantorDocument: 'Guarantor Document',
  TenantInsurance: 'Tenant Insurance',
  TenantNotice: 'Tenant Notice',
  TenantMaintenancePhoto: 'Tenant Maintenance Photo',
  DepositDisputeEvidence: 'Deposit Dispute Evidence',
  RightToRentID: 'Right to Rent ID (Legacy)',
  Other: 'Other Document',
};

const DOCUMENT_CATEGORIES = {
  compliance: { label: 'Property MOT', color: 'bg-blue-100 text-blue-700' },
  tenancy: { label: 'Tenancy', color: 'bg-purple-100 text-purple-700' },
  financial: { label: 'Financial', color: 'bg-green-100 text-green-700' },
  inventory: { label: 'Inventory', color: 'bg-orange-100 text-orange-700' },
  tenant_provided: {
    label: 'Tenant Provided',
    color: 'bg-pink-100 text-pink-700',
  },
  legal: { label: 'Legal', color: 'bg-red-100 text-red-700' },
  general: { label: 'General', color: 'bg-gray-100 text-gray-700' },
};

const getVisibilityBadgeLabel = (document) => {
  if (document.visibility === 'tenant_visible') {
    return 'Shared with tenant';
  }

  if (document.uploaded_by === 'tenant') {
    return 'Private';
  }

  return 'Landlord only';
};

const getTenancyNames = (tenancy) =>
  Array.isArray(tenancy?.tenants)
    ? tenancy.tenants.map((tenant) => tenant?.name).filter(Boolean)
    : [];

const getDocumentTenancyLabel = (document) => {
  if (Array.isArray(document?.tenants) && document.tenants.length) {
    return document.tenants
      .map((tenant) => tenant?.name || tenant?.email)
      .filter(Boolean)
      .join(', ');
  }
  return '';
};

export const PropertyDocuments = () => {
  useEffect(() => { document.title = 'Property Documents - SIYAK'; }, []);
  const { propertyId } = useParams();
  const navigate = useNavigate();
  const { isLandlord, isAdmin, user, refreshUser } = useAuth();

  const [property, setProperty] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [compliance, setCompliance] = useState(null);
  const [accountMetrics, setAccountMetrics] = useState(user?.metrics || {});
  const [dismissedHintVersion, setDismissedHintVersion] = useState(0);
  const [tenancies, setTenancies] = useState([]);
  const [selectedTenancyId, setSelectedTenancyId] = useState(ALL_TENANCIES_VALUE);
  const [rightToRentChecked, setRightToRentChecked] = useState(false);
  const [rraInfoSheetSentChecked, setRraInfoSheetSentChecked] = useState(false);
  const [howToRentServedChecked, setHowToRentServedChecked] = useState(false);
  const [icoRegisteredChecked, setIcoRegisteredChecked] = useState(false);
  const [alarmsChecked, setAlarmsChecked] = useState(false);
  const [periodicTenancyChecked, setPeriodicTenancyChecked] = useState(false);
  const [section13ProcessChecked, setSection13ProcessChecked] = useState(false);
  const [advanceRentChecked, setAdvanceRentChecked] = useState(false);
  const [noBiddingChecked, setNoBiddingChecked] = useState(false);
  const [noDiscriminationChecked, setNoDiscriminationChecked] = useState(false);
  const [petsProcessChecked, setPetsProcessChecked] = useState(false);
  const [savingComplianceEvidence, setSavingComplianceEvidence] = useState(false);
  const [savingDeclarationField, setSavingDeclarationField] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const [sharing, setSharing] = useState(null);
  const [expandedRemedy, setExpandedRemedy] = useState(null);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [forcedUploadDocumentType, setForcedUploadDocumentType] = useState(null);
  const [forcedUploadCustomLabel, setForcedUploadCustomLabel] = useState(null);
  const [motBypassDialogOpen, setMotBypassDialogOpen] = useState(false);
  const [savingMotBypass, setSavingMotBypass] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const isMotBypassed = Boolean(property?.mot_bypassed || compliance?.mot_bypassed);

  const getRemediationUploadPreset = (rule) => {
    if (!rule) {
      return { documentType: null, customLabel: null };
    }

    if (rule.rule_id === 'how_to_rent') {
      return {
        documentType: 'DispatchReceipt',
        customLabel: 'How to Rent Guide',
      };
    }

    if (rule.rule_id === 'rra_info_sheet') {
      return {
        documentType: 'DispatchReceipt',
        customLabel: "Renters' Rights Act Information Sheet 2026",
      };
    }

    return {
      documentType: rule.missing_document_type || null,
      customLabel: null,
    };
  };

  const getDeclarationBinding = (fieldKey) => {
    const fieldBindings = {
      alarms_checked: { setter: setAlarmsChecked },
      periodic_tenancy_checked: { setter: setPeriodicTenancyChecked },
      section_13_process_checked: { setter: setSection13ProcessChecked },
      advance_rent_checked: { setter: setAdvanceRentChecked },
      no_bidding_checked: { setter: setNoBiddingChecked },
      no_discrimination_checked: { setter: setNoDiscriminationChecked },
      pets_process_checked: { setter: setPetsProcessChecked },
    };

    return fieldBindings[fieldKey] || null;
  };

  useEffect(() => {
    if (!isLandlord() && !isAdmin()) {
      navigate('/');
      return;
    }
    fetchData();
    refreshUser?.(true).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyId, selectedTenancyId]);

  useEffect(() => {
    setAccountMetrics(user?.metrics || {});
  }, [user?.metrics]);

  const getErrorMessage = (error) => {
    if (error.response?.data?.detail) {
      return error.response.data.detail;
    } else if (Array.isArray(error.response?.data)) {
      return error.response.data[0]?.msg || 'An error occurred';
    } else if (error.response?.data?.msg) {
      return error.response.data.msg;
    }
    return 'An error occurred';
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const complianceRequestConfig = {
        withCredentials: true,
        ...(selectedTenancyId !== ALL_TENANCIES_VALUE
          ? { params: { tenancy_id: selectedTenancyId } }
          : {}),
      };

      const [propertyRes, docsRes, complianceRes, tenanciesRes] = await Promise.all([
        axios.get(`${API_URL}/api/properties/${propertyId}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/documents/properties/${propertyId}/documents`, { withCredentials: true }),
        axios.get(`${API_URL}/api/documents/compliance/evaluate/${propertyId}`, complianceRequestConfig),
        axios.get(`${API_URL}/api/tenancies/property/${propertyId}`, { withCredentials: true }),
      ]);

      setProperty(propertyRes.data);
      setRightToRentChecked(Boolean(propertyRes.data?.right_to_rent_checked));
      setRraInfoSheetSentChecked(Boolean(propertyRes.data?.rra_info_sheet_sent_checked));
      setHowToRentServedChecked(Boolean(propertyRes.data?.how_to_rent_served_checked));
      setIcoRegisteredChecked(Boolean(propertyRes.data?.ico_registered_checked));
      setAlarmsChecked(Boolean(propertyRes.data?.alarms_checked));
      setPeriodicTenancyChecked(Boolean(propertyRes.data?.periodic_tenancy_checked));
      setSection13ProcessChecked(Boolean(propertyRes.data?.section_13_process_checked));
      setAdvanceRentChecked(Boolean(propertyRes.data?.advance_rent_checked));
      setNoBiddingChecked(Boolean(propertyRes.data?.no_bidding_checked));
      setNoDiscriminationChecked(Boolean(propertyRes.data?.no_discrimination_checked));
      setPetsProcessChecked(Boolean(propertyRes.data?.pets_process_checked));
      // Safely extract documents array
      const docsList = Array.isArray(docsRes.data?.documents) ? docsRes.data.documents : [];
      setDocuments(docsList);
      setCompliance(complianceRes.data);
      setAccountMetrics(complianceRes.data?.metrics || user?.metrics || {});
      const fetchedTenancies = Array.isArray(tenanciesRes.data?.tenancies) ? tenanciesRes.data.tenancies : [];
      setTenancies(fetchedTenancies);
      if (
        selectedTenancyId !== ALL_TENANCIES_VALUE
        && !fetchedTenancies.some((tenancy) => tenancy.id === selectedTenancyId)
      ) {
        setSelectedTenancyId(ALL_TENANCIES_VALUE);
      }
    } catch (error) {
      const errorMsg = getErrorMessage(error);
      toast.error(errorMsg || 'Failed to load documents');
      // Ensure documents is always an array even on error
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (document) => {
    setSelectedDocument(document);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedDocument) return;

    try {
      setDeleting(selectedDocument.id);
      console.log(`Deleting document: ${selectedDocument.id}`);
      const response = await axios.delete(`${API_URL}/api/documents/${selectedDocument.id}`, {
        withCredentials: true,
      });
      console.log('Delete response:', response.status, response.data);
      toast.success('Document deleted successfully');
      setDocuments(documents.filter((doc) => doc.id !== selectedDocument.id));
      setDeleteDialogOpen(false);

      // Refresh compliance status
      const complianceRes = await axios.get(
        `${API_URL}/api/documents/compliance/evaluate/${propertyId}`,
        { withCredentials: true }
      );
      setCompliance(complianceRes.data);
    } catch (error) {
      console.error('Delete error:', error.response?.status, error.response?.data);
      const errorMsg = getErrorMessage(error);
      toast.error(errorMsg || 'Failed to delete document');
    } finally {
      setDeleting(null);
      setSelectedDocument(null);
    }
  };

  const handleUploadSuccess = async () => {
    // Refresh documents and compliance
    await fetchData();
  };

  const handleSaveComplianceEvidence = async () => {
    try {
      setSavingComplianceEvidence(true);
      await axios.put(
        `${API_URL}/api/properties/${propertyId}`,
        {
          right_to_rent_checked: rightToRentChecked,
          rra_info_sheet_sent_checked: rraInfoSheetSentChecked,
          how_to_rent_served_checked: howToRentServedChecked,
          ico_registered_checked: icoRegisteredChecked,
          alarms_checked: alarmsChecked,
          periodic_tenancy_checked: periodicTenancyChecked,
          section_13_process_checked: section13ProcessChecked,
          advance_rent_checked: advanceRentChecked,
          no_bidding_checked: noBiddingChecked,
          no_discrimination_checked: noDiscriminationChecked,
          pets_process_checked: petsProcessChecked,
        },
        { withCredentials: true }
      );
      toast.success('Compliance evidence updated');
      await fetchData();
    } catch (error) {
      const errorMsg = getErrorMessage(error);
      toast.error(errorMsg || 'Could not save compliance evidence');
    } finally {
      setSavingComplianceEvidence(false);
    }
  };

  const handleDeclarationToggle = async (fieldKey, setter, newValue) => {
    setter(newValue);
    setSavingDeclarationField(fieldKey);
    try {
      await axios.put(
        `${API_URL}/api/properties/${propertyId}`,
        { [fieldKey]: newValue },
        { withCredentials: true }
      );
      // Only refresh compliance — avoids full page remount
      const complianceRes = await axios.get(
        `${API_URL}/api/documents/compliance/evaluate/${propertyId}`,
        { withCredentials: true }
      );
      setCompliance(complianceRes.data);
    } catch (error) {
      setter(!newValue); // revert on failure
      const errorMsg = getErrorMessage(error);
      toast.error(errorMsg || 'Could not save — please try again');
    } finally {
      setSavingDeclarationField(null);
    }
  };

  const handleSetMotBypass = async (nextValue) => {
    try {
      setSavingMotBypass(true);
      await axios.put(
        `${API_URL}/api/properties/${propertyId}`,
        { mot_bypassed: nextValue },
        { withCredentials: true }
      );
      toast.success(
        nextValue
          ? 'Property MOT disabled for this property'
          : 'Property MOT re-enabled'
      );
      setMotBypassDialogOpen(false);
      await fetchData();
    } catch (error) {
      const errorMsg = getErrorMessage(error);
      toast.error(errorMsg || 'Could not update Property MOT setting');
    } finally {
      setSavingMotBypass(false);
    }
  };

  const handleToggleVisibility = async (document) => {
    const nextVisibility =
      document.visibility === 'tenant_visible' ? 'landlord_only' : 'tenant_visible';

    try {
      setSharing(document.id);
      await axios.patch(
        `${API_URL}/api/documents/${document.id}/visibility`,
        null,
        {
          params: { visibility: nextVisibility },
          withCredentials: true,
        }
      );

      setDocuments((prev) =>
        prev.map((doc) =>
          doc.id === document.id ? { ...doc, visibility: nextVisibility } : doc
        )
      );
      toast.success(
        nextVisibility === 'tenant_visible'
          ? 'Document shared with tenant'
          : 'Document unshared from tenant'
      );
    } catch (error) {
      const errorMsg = getErrorMessage(error);
      toast.error(errorMsg || 'Failed to update visibility');
    } finally {
      setSharing(null);
    }
  };

  if (!isLandlord() && !isAdmin()) {
    return null;
  }

  if (loading) {
    return (
      <Layout>
        <div className="max-w-6xl mx-auto py-8 px-4">
          <div className="flex items-center justify-center min-h-96">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        </div>
      </Layout>
    );
  }

  const selectedTenancy = tenancies.find((tenancy) => tenancy.id === selectedTenancyId) || null;
  const selectedTenancyStartLabel = selectedTenancy ? formatDateForDisplay(selectedTenancy.start_date) : null;
  const selectedTenancyLabel = selectedTenancy
    ? [
        getTenancyNames(selectedTenancy).join(', ') || 'Tenant',
        selectedTenancyStartLabel ? `Start ${selectedTenancyStartLabel}` : 'Start not set',
        `Deposit ${selectedTenancy.deposit_taken ? 'Yes' : 'No'}`,
      ].join(' · ')
    : `All active tenancies${tenancies.length ? ` · ${tenancies.length}` : ''}`;
  const displayedDocuments = [...documents].sort((left, right) => {
    if (!selectedTenancy) {
      return new Date(right.created_at).getTime() - new Date(left.created_at).getTime();
    }

    const getRank = (document) => {
      if (document.tenancy_id === selectedTenancy.id) return 0;
      if (!document.tenancy_id) return 1;
      return 2;
    };

    const rankDifference = getRank(left) - getRank(right);
    if (rankDifference !== 0) return rankDifference;
    return new Date(right.created_at).getTime() - new Date(left.created_at).getTime();
  });
  const documentHints = getUpgradePromptsForPlacement(accountMetrics, 'documents');
  const handleDismissHint = (hintId) => {
    dismissUpgradePrompt(hintId);
    setDismissedHintVersion((value) => value + 1);
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto py-8 px-4 space-y-6">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/landlord')}
              className="shrink-0"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="min-w-0">
              <h1 className="text-3xl font-bold">
                {property?.title || 'Property Documents'}
              </h1>
              <p className="text-gray-600 mt-1">
                {formatPropertyAddress(property?.address, property?.postcode)}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 shrink-0">
            {isMotBypassed ? (
              <Button
                variant="outline"
                onClick={() => handleSetMotBypass(false)}
                disabled={savingMotBypass}
                className="gap-2"
              >
                {savingMotBypass ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                Re-enable Property MOT
              </Button>
            ) : (
              <Button
                variant="outline"
                onClick={() => setMotBypassDialogOpen(true)}
                className="gap-2"
              >
                <EyeOff className="w-4 h-4" />
                I&apos;ve done my own MOT test
              </Button>
            )}
          </div>
        </div>

        {documentHints.map((hint) => (
          <UpgradeHintCard
            key={`${hint.id}-${dismissedHintVersion}`}
            message={hint.message}
            ctaLabel={hint.ctaLabel}
            onDismiss={() => handleDismissHint(hint.id)}
          />
        ))}

        {isMotBypassed && (
          <Card className="border-slate-200 bg-slate-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-slate-900">
                <EyeOff className="w-5 h-5 text-slate-600" />
                Property MOT disabled
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-slate-700">
                You&apos;ve marked this property as already checked outside SIYAK. MOT score, reminders,
                remediation steps, and checklist actions are hidden, and this page now works as a
                simple document upload and storage area.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Tenancies (tenancy information for compliance) */}
        {!isMotBypassed && (
        <Card>
          <CardHeader>
            <CardTitle>Tenancy Information</CardTitle>
            <CardDescription className="text-sm text-muted-foreground">Tenancy details used for Property MOT calculations. This page is read-only; update tenancy records in Tenancy Management.</CardDescription>
            {tenancies.length > 0 && (
              <div className="mt-4 flex flex-col gap-3 sm:max-w-md">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Compliance scope</p>
                <Select value={selectedTenancyId} onValueChange={setSelectedTenancyId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select tenancy scope" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL_TENANCIES_VALUE}>All active tenancies</SelectItem>
                    {tenancies.map((tenancy) => {
                      const tenancyLabel = formatDateForDisplay(tenancy.start_date);
                      return (
                        <SelectItem key={tenancy.id} value={tenancy.id}>
                          {[(getTenancyNames(tenancy).join(', ') || 'Tenant'), tenancyLabel ? `Start ${tenancyLabel}` : 'Start not set', `Deposit ${tenancy.deposit_taken ? 'Yes' : 'No'}`].join(' · ')}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                {selectedTenancy ? (
                  <div className="rounded-md bg-muted/40 px-3 py-3 text-sm">
                    <p className="font-medium text-navy-900">{getTenancyNames(selectedTenancy).join(', ') || 'Tenant'}</p>
                    <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                      <span>Start: {selectedTenancyStartLabel || 'Not set'}</span>
                      <span>Deposit taken: {selectedTenancy.deposit_taken ? 'Yes' : 'No'}</span>
                      {selectedTenancy.deposit_taken && selectedTenancy.deposit_amount != null ? (
                        <span>
                          Deposit amount: £{Number(selectedTenancy.deposit_amount).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      ) : null}
                    </div>
                    <p className="mt-3 text-xs text-muted-foreground">
                      Tenancy dates are read-only here. Update start dates from Tenancy Management.
                    </p>
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    Select a tenancy to review its start date and deposit context. Property-wide MOT checks still apply to the whole property.
                  </p>
                )}
                <p className="text-xs text-muted-foreground">
                  Property-wide MOT checks still apply. Tenancy-dependent rules use the selected tenancy context.
                </p>
              </div>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            {tenancies.length === 0 && (
              <p className="text-sm text-gray-500">
                No active tenancies found for this property.
              </p>
            )}
            <div className="border border-gray-200 rounded-lg p-3 space-y-3">
              <h4 className="text-sm font-semibold text-gray-900">Compliance Evidence</h4>
              {[
                { key: 'alarms_checked', state: alarmsChecked, setter: setAlarmsChecked, label: 'Smoke and CO alarm requirements met and tested at tenancy start' },
                { key: 'periodic_tenancy_checked', state: periodicTenancyChecked, setter: setPeriodicTenancyChecked, label: 'Periodic tenancy transition prepared for pre-May 2026 tenancies' },
                { key: 'section_13_process_checked', state: section13ProcessChecked, setter: setSection13ProcessChecked, label: 'Section 13 Form 4A process understood and used for rent increases' },
                { key: 'advance_rent_checked', state: advanceRentChecked, setter: setAdvanceRentChecked, label: 'Advance rent limit (max 1 month) applied for new tenancies' },
                { key: 'no_bidding_checked', state: noBiddingChecked, setter: setNoBiddingChecked, label: 'No rental bidding above advertised price in listings or workflows' },
                { key: 'no_discrimination_checked', state: noDiscriminationChecked, setter: setNoDiscriminationChecked, label: 'No discrimination based on benefits or children in listings/process' },
                { key: 'pets_process_checked', state: petsProcessChecked, setter: setPetsProcessChecked, label: 'Pet request process in place with response within 28 days' },
              ].map(({ key, state, setter, label }) => (
                <label key={key} className="flex items-center gap-2 text-sm text-gray-800 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded"
                    checked={state}
                    disabled={savingDeclarationField === key}
                    onChange={(e) => handleDeclarationToggle(key, setter, e.target.checked)}
                  />
                  <span className="flex-1">{label}</span>
                  {savingDeclarationField === key && (
                    <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                  )}
                </label>
              ))}

            </div>
          </CardContent>
        </Card>
        )}

        {/* Compliance Status */}
        {compliance && !isMotBypassed && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>MOT Status</span>
                <ComplianceBadge
                  status={compliance.mot_status || (compliance.regulatory_compliant ? 'compliant' : 'non-compliant')}
                  missingCount={
                    compliance.failed_rules?.length || compliance.missing_documents?.length || 0
                  }
                  expiredCount={compliance.expired_documents?.length || 0}
                  expiringSoonCount={compliance.expiring_documents?.length || 0}
                />
              </CardTitle>
              {tenancies.length > 0 && (
                <CardDescription className="text-sm text-muted-foreground">
                  Checking compliance for <strong>{selectedTenancyLabel}</strong> alongside property-wide requirements.
                </CardDescription>
              )}
              {tenancies.length > 0 && (
                <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                  <Badge variant="outline" className="border-royal-200 text-royal-700">Selected tenancy</Badge>
                  <span>Tenancy-tagged items depend on the selected tenancy.</span>
                  <Badge variant="outline" className="border-slate-200 text-slate-700">Property-wide</Badge>
                  <span>Property-tagged items apply to the whole property.</span>
                </div>
              )}
              {(typeof compliance.score_pct === 'number' || typeof compliance.score_grade === 'string') && (
                <div className="mt-2 text-sm text-muted-foreground">
                  <span className="font-medium text-navy-900">Score:</span>{' '}
                  {(compliance.score_passed ?? 0)} / {(compliance.score_total ?? 0)} ({(compliance.score_pct ?? 0)}%)
                  {' • '}
                  <span className="font-medium text-navy-900">Grade:</span> {compliance.score_grade || 'F'}
                </div>
              )}
            </CardHeader>
            <CardContent className="space-y-4">
              {compliance.expired_documents?.length > 0 && (
                <div className="flex gap-3 p-4 bg-orange-50 border border-orange-200 rounded-lg">
                  <AlertCircle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-orange-800">
                    <strong>Expired Documents:</strong>
                    <ul className="mt-2 space-y-1">
                      {compliance.expired_documents.map((doc) => (
                        <li key={doc}>
                          • {DOCUMENT_TYPE_DISPLAY[doc.document_type] || doc.document_type || doc}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {compliance.regulatory_compliant && (
                <div className="flex gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-green-800">
                    <strong>All MOT requirements met!</strong> Your property
                    is MOT Pass.
                  </div>
                </div>
              )}

              {compliance.failed_rules?.length > 0 && (
                <div className="space-y-3">
                  <h4 className="font-semibold text-gray-900">Remediation steps</h4>
                  {Object.entries(
                    compliance.failed_rules.reduce((acc, rule) => {
                      const section = rule.section_label || 'Other';
                      if (!acc[section]) {
                        acc[section] = [];
                      }
                      acc[section].push(rule);
                      return acc;
                    }, {})
                  ).map(([sectionLabel, rules]) => (
                    <div key={sectionLabel} className="space-y-3">
                      <div className="border-b pb-1">
                        <p className="text-sm font-semibold text-muted-foreground">{sectionLabel}</p>
                      </div>
                      {rules.map((rule) => (
                        <div key={rule.rule_id} className="border border-red-200 rounded-md p-4 space-y-2 bg-red-50/40">
                          <div className="flex flex-wrap items-start justify-between gap-2">
                            <div className="flex items-start gap-2">
                              <span className="inline-flex flex-shrink-0 h-5 w-5 items-center justify-center rounded-full bg-red-600 text-white text-xs font-bold">!</span>
                              <p className="text-sm font-medium text-red-900">{rule.text || rule.rule_id}</p>
                            </div>
                            <div className="flex flex-wrap items-center gap-2">
                              <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold ${rule.scope === 'tenancy' ? 'border-royal-200 bg-royal-50 text-royal-700' : 'border-slate-200 bg-slate-50 text-slate-700'}`}>
                                {rule.scope === 'tenancy' ? 'Selected tenancy' : 'Property-wide'}
                              </span>
                              <span className="inline-flex items-center rounded-full border border-red-200 bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-800">
                                Failing MOT
                              </span>
                              {rule.requires_document_upload && (
                                <span className="inline-flex items-center rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-xs font-semibold text-blue-800">
                                  Document upload required
                                </span>
                              )}
                              {rule.requires_checkbox_declaration && (
                                <span className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-800">
                                  Checkbox declaration
                                </span>
                              )}
                            </div>
                          </div>

                          <div className="mt-2">
                            <button
                              onClick={() => setExpandedRemedy(expandedRemedy === rule.rule_id ? null : rule.rule_id)}
                              className="flex items-center gap-2 text-sm font-medium text-royal-600 hover:text-royal-800 transition-colors"
                            >
                              {expandedRemedy === rule.rule_id ? '▼ Hide steps' : '▶ Show me how to fix this'}
                            </button>

                            {expandedRemedy === rule.rule_id && Array.isArray(rule.steps) && rule.steps.length > 0 && (
                              <div className="mt-3 bg-blue-50 border border-blue-100 rounded-xl p-4">
                                <ol className="space-y-2">
                                  {rule.steps.map((stepText, i) => (
                                    <li key={`${rule.rule_id}-${i}`} className="flex gap-3 text-sm text-navy-800">
                                      <span className="flex-shrink-0 w-5 h-5 bg-royal-600 text-white rounded-full flex items-center justify-center text-xs font-bold mt-0.5">
                                        {i + 1}
                                      </span>
                                      <span>{stepText}</span>
                                    </li>
                                  ))}
                                </ol>
                                {rule.action_url && (
                                  <a
                                    href={rule.action_url}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="inline-flex items-center gap-1 mt-4 text-sm font-medium text-royal-600 hover:underline"
                                  >
                                    {rule.action_label || 'Guidance'} ↗
                                  </a>
                                )}
                                {(rule.alternative_step || rule.siyak_step) && (rule.alternative_action || rule.siyak_action) && (
                                  <div className="mt-4 border-t border-blue-200 pt-3">
                                    <p className="text-xs font-semibold text-emerald-700 uppercase tracking-wide mb-1">Easy alternative</p>
                                    <p className="text-sm text-navy-800 mb-2">{rule.alternative_step || rule.siyak_step}</p>
                                    <a
                                      href={(rule.alternative_action || rule.siyak_action).url}
                                      className="inline-flex items-center gap-1 text-sm font-medium text-emerald-700 hover:underline border border-emerald-200 bg-emerald-50 rounded-lg px-3 py-1.5"
                                    >
                                      🚀 {(rule.alternative_action || rule.siyak_action).label} ↗
                                    </a>
                                  </div>
                                )}
                                {rule.requires_document_upload && (
                                  <div className="mt-4 border-t border-blue-200 pt-3 flex flex-wrap items-start justify-between gap-3 max-w-full">
                                    <p className="text-sm text-navy-800 min-w-0 flex-1">Once you have the document, upload it here to clear this from your MOT.</p>
                                    <button
                                      onClick={() => {
                                        const preset = getRemediationUploadPreset(rule);
                                        setForcedUploadDocumentType(preset.documentType);
                                        setForcedUploadCustomLabel(preset.customLabel);
                                        setUploadModalOpen(true);
                                      }}
                                      className="max-w-full flex-shrink-0 inline-flex flex-wrap items-center gap-1.5 rounded-lg border border-royal-300 bg-white px-3 py-1.5 text-sm font-medium text-royal-700 hover:bg-royal-50 transition-colors"
                                    >
                                      <Upload className="h-3.5 w-3.5" />
                                      Upload evidence
                                    </button>
                                  </div>
                                )}
                                {rule.requires_checkbox_declaration && rule.declaration_field && getDeclarationBinding(rule.declaration_field) && (
                                  <div className="mt-4 border-t border-blue-200 pt-3 flex flex-wrap items-start justify-between gap-3 max-w-full">
                                    <p className="text-sm text-navy-800 min-w-0 flex-1">Complete the checklist declaration here to clear this from your MOT.</p>
                                    <button
                                      onClick={() => {
                                        const binding = getDeclarationBinding(rule.declaration_field);
                                        if (!binding) return;
                                        handleDeclarationToggle(rule.declaration_field, binding.setter, true);
                                      }}
                                      disabled={savingDeclarationField === rule.declaration_field}
                                      className="max-w-full flex-shrink-0 inline-flex flex-wrap items-center gap-1.5 rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-sm font-medium text-amber-700 hover:bg-amber-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                                    >
                                      {savingDeclarationField === rule.declaration_field ? (
                                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                      ) : (
                                        <CheckCircle2 className="h-3.5 w-3.5" />
                                      )}
                                      I have completed these steps
                                    </button>
                                  </div>
                                )}
                                {rule.requires_checkbox_declaration && rule.declaration_field && !getDeclarationBinding(rule.declaration_field) && (
                                  <div className="mt-4 border-t border-blue-200 pt-3 flex flex-wrap items-start justify-between gap-3 max-w-full">
                                    <p className="text-sm text-navy-800 min-w-0 flex-1">
                                      {rule.rule_id === 'property_licensing'
                                        ? 'Select Yes or No for council licensing in your property settings to clear this from your MOT.'
                                        : 'Update this in your property settings to clear it from your MOT.'}
                                    </p>
                                    <a
                                      href="/landlord#properties"
                                      className="max-w-full flex-shrink-0 inline-flex flex-wrap items-center gap-1.5 rounded-lg border border-royal-300 bg-white px-3 py-1.5 text-sm font-medium text-royal-700 hover:bg-royal-50 transition-colors"
                                    >
                                      {rule.action_label || 'Update property settings'} →
                                    </a>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Documents List */}
        <div className="space-y-4">
          <div>
            <h2 className="text-xl font-bold">Documents ({documents.length})</h2>
            {isMotBypassed && (
              <p className="text-sm text-gray-600 mt-1">
                Property MOT is disabled for this property. Documents uploaded here are stored without SIYAK MOT checks.
              </p>
            )}
          </div>

          {documents.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <FileText className="w-12 h-12 text-gray-400 mb-4" />
                <p className="text-gray-600 mb-4">No documents uploaded yet</p>
                <Button onClick={() => {
                  setForcedUploadDocumentType(null);
                  setForcedUploadCustomLabel(null);
                  setUploadModalOpen(true);
                }}>
                  Upload Your First Document
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {displayedDocuments.map((document) => (
                <Card
                  key={document.id}
                  className="hover:shadow-md transition-shadow"
                >
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between">
                      <div className="flex gap-4 flex-1">
                        <FileText className="w-10 h-10 text-blue-600 flex-shrink-0 mt-1" />
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="font-semibold">
                              {document.custom_label || DOCUMENT_TYPE_DISPLAY[
                                document.document_type
                              ] || document.document_type}                              {document.requirement_level === 'MANDATORY' && (
                                <span className="ml-2 text-xs font-bold text-red-600 bg-red-50 px-2 py-1 rounded">
                                  Required
                                </span>
                              )}                            </h3>
                            {document.category &&
                              DOCUMENT_CATEGORIES[document.category] && (
                                <Badge
                                  className={
                                    DOCUMENT_CATEGORIES[document.category]
                                      .color
                                  }
                                >
                                  {
                                    DOCUMENT_CATEGORIES[document.category]
                                      .label
                                  }
                                </Badge>
                              )}
                            <Badge
                              className={
                                document.visibility === 'tenant_visible'
                                  ? 'bg-royal-100 text-royal-700'
                                  : 'bg-gray-100 text-gray-700'
                              }
                            >
                              {getVisibilityBadgeLabel(document)}
                            </Badge>
                            <Badge
                              variant="outline"
                              className={
                                document.tenancy_id
                                  ? document.tenancy_id === selectedTenancy?.id
                                    ? 'border-royal-200 text-royal-700'
                                    : 'border-amber-200 text-amber-700'
                                  : 'border-slate-200 text-slate-700'
                              }
                            >
                              {document.tenancy_id
                                ? document.tenancy_id === selectedTenancy?.id
                                  ? 'Selected tenancy'
                                  : 'Other tenancy'
                                : 'Property-wide'}
                            </Badge>
                          </div>

                          {document.tenancy_id && getDocumentTenancyLabel(document) && (
                            <div className="mb-2 text-xs text-muted-foreground">
                              Linked tenancy: <strong>{getDocumentTenancyLabel(document)}</strong>
                              {document.tenancy_start_date ? ` · Start ${formatDateForDisplay(document.tenancy_start_date)}` : ''}
                            </div>
                          )}

                          <div className="flex flex-wrap gap-3 text-sm text-gray-600 mb-3">
                            {document.custom_label && (
                              <div className="text-xs text-gray-500">
                                Type: {DOCUMENT_TYPE_DISPLAY[
                                  document.document_type
                                ] || document.document_type}
                              </div>
                            )}
                            <div className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              Uploaded{' '}
                              {formatDistanceToNow(
                                parseISO(document.created_at),
                                { addSuffix: true }
                              )}
                            </div>
                            {document.expiry_date && (
                              <DocumentExpiryWarning
                                expiryDate={document.expiry_date}
                                documentName={
                                  DOCUMENT_TYPE_DISPLAY[
                                    document.document_type
                                  ] || document.document_type
                                }
                              />
                            )}
                          </div>

                          {document.file_path && (
                            <div className="text-xs text-gray-500">
                              {document.file_path.split('/').pop()}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="flex gap-2 ml-4">
                        {document.uploaded_by !== 'tenant' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleToggleVisibility(document)}
                            disabled={sharing === document.id}
                            title={
                              document.visibility === 'tenant_visible'
                                ? 'Unshare from tenant'
                                : 'Share with tenant'
                            }
                          >
                            {sharing === document.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : document.visibility === 'tenant_visible' ? (
                              <EyeOff className="w-4 h-4" />
                            ) : (
                              <Share2 className="w-4 h-4" />
                            )}
                          </Button>
                        )}
                        {document.file_path && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const url = `${API_URL}/${document.file_path}`;
                              window.open(url, '_blank');
                            }}
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteClick(document)}
                          disabled={deleting === document.id}
                          className="hover:bg-red-50 hover:text-red-600 hover:border-red-200"
                        >
                          {deleting === document.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Upload Modal */}
      <DocumentUploadModal
        propertyId={propertyId}
        property={property}
        compliance={compliance}
        tenancies={tenancies}
        defaultTenancyId={selectedTenancy?.id || null}
        open={uploadModalOpen}
        forcedDocumentType={forcedUploadDocumentType}
        forcedCustomLabel={forcedUploadCustomLabel}
        onOpenChange={(nextOpen) => {
          setUploadModalOpen(nextOpen);
          if (!nextOpen) {
            setForcedUploadDocumentType(null);
            setForcedUploadCustomLabel(null);
          }
        }}
        onSuccess={handleUploadSuccess}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Document?</AlertDialogTitle>
            <AlertDialogDescription>
              {selectedDocument?.requirement_level === 'MANDATORY' ? (
                <div className="space-y-4">
                  <p className="text-red-700 font-semibold">
                    ⚠️ Warning: This is a mandatory MOT document
                  </p>
                  <p>
                    You are deleting a required document. Your property may become
                    fail its MOT if a valid replacement is not uploaded.
                  </p>
                  <p>This action cannot be undone. Are you sure?</p>
                </div>
              ) : (
                <p>This action cannot be undone. The document will be permanently deleted.</p>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex gap-4 justify-end pt-4">
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </div>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={motBypassDialogOpen} onOpenChange={setMotBypassDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disable Property MOT for this property?</AlertDialogTitle>
            <AlertDialogDescription>
              SIYAK will stop showing MOT score, remediation, reminders, and checklist checks for this property.
              The documents page will stay available as a simple upload and storage area until you re-enable Property MOT.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex gap-4 justify-end pt-4">
            <AlertDialogCancel disabled={savingMotBypass}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleSetMotBypass(true)}
              className="bg-slate-700 hover:bg-slate-800"
              disabled={savingMotBypass}
            >
              {savingMotBypass ? 'Disabling...' : 'Disable Property MOT'}
            </AlertDialogAction>
          </div>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
};
